import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Download, Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ItemPicker, SupplierPicker } from "@/components/pickers";
import type { PartnerRow } from "@/features/partners/api";
import { buyersQuery } from "@/features/planning/api";
import { CoverageDetail } from "@/features/planning/CoverageDetail";
import { CoverageGrid } from "@/features/planning/CoverageGrid";
import {
  coverageCountsQuery,
  coverageListQuery,
  coverageRowQuery,
  coverageStatuses,
  isoWeek,
  listCoverage,
  statusTone,
  weekDate,
  WEEKS,
  type CoverageParams,
  type CoverageRow,
  type CoverageStatus,
} from "@/features/planning/coverage";
import { RunBar } from "@/features/planning/RunBar";
import { validateCoverageSearch, type CoverageSearch } from "@/features/planning/search";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { exportFileName, exportToXlsx, EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { sessionContextQuery } from "@/lib/session";

// Component coverage (PX-PLN-210): every planned item of the active company
// with its status, key figures and projected stock week by week (26 weeks,
// with planned orders), from the latest MRP run. Filters: status chips (with
// counts), code prefix, supplier, buyer, bought / made. A row opens the
// item's detail: weekly demand / supply / planned / stock, the run's lines,
// exceptions and planning parameters. Export to Excel with every week.

export const Route = createFileRoute("/_app/planning/stock-coverage")({
  validateSearch: validateCoverageSearch,
  loader: async ({ context }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.prefetchQuery(coverageCountsQuery(companyId));
  },
  component: StockCoveragePage,
});

const toneDot: Record<string, string> = {
  danger: "bg-(--status-danger-fg)",
  warning: "bg-(--status-warning-fg)",
  info: "bg-(--status-info-fg)",
  success: "bg-(--status-success-fg)",
  neutral: "bg-ink-faint",
  violet: "bg-(--status-violet-fg)",
};

function StockCoveragePage() {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { company } = useActiveCompany();
  const companyId = company?.id ?? "";
  const setSearch = useCallback(
    (patch: Partial<CoverageSearch>) => void navigate({ search: (prev: CoverageSearch) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const statuses = (search.status?.split(",") ?? []) as CoverageStatus[];
  const params: CoverageParams = {
    companyId,
    q: search.q ?? "",
    statuses,
    supplierId: search.supplier ?? null,
    buyerId: search.buyer ?? null,
    procurement: search.proc ?? null,
  };
  const query = useInfiniteQuery(coverageListQuery(params));
  const counts = useQuery(coverageCountsQuery(companyId)).data ?? {};
  const loaded = query.data?.pages.flatMap((p) => p.rows) ?? [];
  const fromList = search.item ? loaded.find((r) => r.item_id === search.item) : undefined;
  const single = useQuery({ ...coverageRowQuery(companyId, search.item ?? ""), enabled: !!search.item && !fromList });
  const selected = fromList ?? single.data ?? null;

  const toggleStatus = (s: CoverageStatus) => {
    const next = statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s];
    setSearch({ status: next.length > 0 ? next.join(",") : undefined });
  };
  const total = Object.values(counts).reduce((a, b) => a + (b ?? 0), 0);

  return (
    <div className={cn("flex min-h-0 grow flex-col gap-3", search.item && "xl:pr-[612px]")}>
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.planning.title")} <span aria-hidden="true">/</span> {t("nav.planning.stockCoverage")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("cover.title")}</h1>
        </div>
        <ExportButton params={params} />
      </header>
      <RunBar companyId={companyId} />

      <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label={t("cover.filterStatus")}>
        <button
          type="button"
          aria-pressed={statuses.length === 0}
          onClick={() => setSearch({ status: undefined })}
          className={chip(statuses.length === 0)}
        >
          {t("cover.allStatuses")}
          <span className="tabular text-[11px] opacity-70">{total}</span>
        </button>
        {coverageStatuses.map((s) => (
          <button key={s} type="button" aria-pressed={statuses.includes(s)} onClick={() => toggleStatus(s)} className={chip(statuses.includes(s))}>
            <span aria-hidden="true" className={cn("size-2 rounded-full", toneDot[statusTone[s]])} />
            {tr(`cover.status.${s}`)}
            <span className="tabular text-[11px] opacity-70">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      <Filters search={search} setSearch={setSearch} />

      <CoverageGrid
        query={query}
        selectedId={search.item}
        onSelect={(r) => setSearch({ item: r?.item_id })}
        emptyAction={
          (search.q || search.status || search.supplier || search.buyer || search.proc) && (
            <button
              type="button"
              onClick={() => setSearch({ q: undefined, status: undefined, supplier: undefined, buyer: undefined, proc: undefined })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          )
        }
      />

      {search.item && (fromList || !single.isPending) && (
        <aside
          aria-label={t("cover.detailLabel")}
          className="fixed inset-x-3 bottom-3 top-16 z-40 overflow-y-auto rounded-2xl border border-border bg-surface p-4 shadow-2xl sm:inset-x-auto sm:inset-y-3 sm:right-3 sm:w-[600px]"
          onKeyDown={(e) => {
            if (e.key === "Escape") setSearch({ item: undefined });
          }}
        >
          <CoverageDetail key={search.item} itemId={search.item} row={selected} companyId={companyId} onClose={() => setSearch({ item: undefined })} />
        </aside>
      )}
    </div>
  );
}

const chip = (on: boolean) =>
  cn(
    "flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-focus",
    on ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink-soft hover:bg-surface-hover hover:text-ink",
  );

function Filters({ search, setSearch }: { search: CoverageSearch; setSearch: (p: Partial<CoverageSearch>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q ?? "");
  const buyers = useQuery(buyersQuery).data ?? [];
  const [supplier, setSupplier] = useState<PartnerRow | null>(null);
  useEffect(() => {
    if (text === (search.q ?? "")) return;
    const id = setTimeout(() => setSearch({ q: text.trim() ? text : undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex h-9 min-w-[220px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <Search size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("cover.searchPlaceholder")}
          aria-label={t("cover.searchPlaceholder")}
          className="min-w-0 grow bg-transparent font-mono text-[13px] text-ink uppercase outline-none placeholder:font-sans placeholder:normal-case placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
      <div className="w-56">
        <ItemPicker
          value={null}
          onChange={(i) => i && setSearch({ item: i.id })}
          label={t("plan.item")}
          filter={(i) => i.item_type !== "service"}
        />
      </div>
      <div className="w-56">
        <SupplierPicker
          value={supplier}
          onChange={(p) => {
            setSupplier(p);
            setSearch({ supplier: p?.id });
          }}
          label={t("cover.col.supplier")}
        />
      </div>
      <label className="flex flex-col gap-1 text-xs text-ink-muted">
        {t("cover.col.buyer")}
        <select
          value={search.buyer ?? ""}
          onChange={(e) => setSearch({ buyer: e.target.value || undefined })}
          className="h-9 rounded-xl border border-border bg-surface px-2.5 text-[13px] text-ink"
          aria-label={t("cover.col.buyer")}
        >
          <option value="">{t("cover.anyBuyer")}</option>
          {buyers.map((b) => (
            <option key={b.user_id} value={b.user_id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-1 rounded-xl bg-surface-muted p-1" role="group" aria-label={t("cover.procurement")}>
        {(["all", "purchase", "manufacture"] as const).map((p) => {
          const on = (search.proc ?? "all") === p;
          return (
            <button
              key={p}
              type="button"
              aria-pressed={on}
              onClick={() => setSearch({ proc: p === "all" ? undefined : p })}
              className={cn("h-7 rounded-lg px-2.5 text-[13px]", on ? "bg-surface font-medium shadow-sm" : "text-ink-muted hover:text-ink")}
            >
              {t(`cover.proc.${p}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Excel with every week: the same server query, page after page. */
function ExportButton({ params }: { params: CoverageParams }) {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      let weekStart = "";
      const weeks: ExportColumn<CoverageRow>[] = Array.from({ length: WEEKS }, (_, w) => ({
        header: `+${w}`,
        kind: "number" as const,
        value: (r: CoverageRow) => r.balance[w] ?? null,
      }));
      const columns: ExportColumn<CoverageRow>[] = [
        { header: t("cover.col.item"), kind: "code", value: (r) => r.item_code },
        { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
        { header: t("cover.col.status"), kind: "text", value: (r) => tr(`cover.status.${r.status}`) },
        { header: t("cover.col.supplier"), kind: "code", value: (r) => r.supplier_code },
        { header: t("cover.col.buyer"), kind: "text", value: (r) => r.buyer_name },
        { header: "UoM", kind: "code", value: (r) => r.uom_code },
        { header: t("cover.col.onHand"), kind: "number", value: (r) => r.on_hand },
        { header: t("cover.col.perDay"), kind: "number", value: (r) => r.avg_daily_demand },
        { header: t("cover.col.cover"), kind: "number", value: (r) => r.days_cover },
        { header: t("cover.col.transit"), kind: "number", value: (r) => r.on_order },
        { header: t("cover.col.nextSupply"), kind: "date", value: (r) => r.next_supply_date },
        { header: t("cover.totalDemand"), kind: "number", value: (r) => r.total_demand },
        ...weeks,
      ];
      // Columns are written after the last page: the week headers are set from the first page.
      await exportToXlsx<CoverageRow>({
        fileName: exportFileName("component-coverage"),
        sheet: t("cover.title"),
        columns,
        signal: new AbortController().signal,
        fetchPage: async (cursor, signal) => {
          const page = await listCoverage(params, cursor ? (JSON.parse(cursor) as { code: string; id: string }) : null, signal, EXPORT_PAGE);
          if (!weekStart && page.rows[0]) {
            weekStart = page.rows[0].week_start;
            weeks.forEach((c, w) => (c.header = t("cover.week", { n: isoWeek(weekDate(weekStart, w)) })));
          }
          return { rows: page.rows, nextCursor: page.nextCursor ? JSON.stringify(page.nextCursor) : null };
        },
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={busy || !params.companyId}
      className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-surface px-3.5 text-[13px] font-medium hover:bg-surface-hover disabled:opacity-50"
    >
      {busy ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Download size={15} aria-hidden="true" />}
      {t("cover.export")}
    </button>
  );
}
